import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { Download, Eye, FilePlus2, FileText } from "lucide-react";
import {
  CreateEyeRecordSchema,
  type CreateEyeRecordInput,
  type EyeRecord,
  type Prescription,
} from "@clinic/types";
import { formatAxis, formatDiopter, formatIop } from "@clinic/ui";
import { api } from "@/lib/api";
import { useMe } from "@/lib/auth";
import { openPrescriptionDoc } from "@/lib/prescriptionDoc";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { FieldError, Input, Label, Textarea } from "@/components/ui/input";
import { SkeletonRows } from "@/components/ui/skeleton";

function EyeValues({ label, eye }: { label: string; eye: EyeRecord["rightEye"] }) {
  return (
    <div
      className="rounded-lg border bg-bg px-4 py-3"
      style={{ borderColor: "var(--accent-subtle)" }}
    >
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-accent-light">
        {label}
      </div>
      <div className="grid grid-cols-5 gap-2 font-mono text-xs">
        {[
          ["SPH", formatDiopter(eye.sph)],
          ["CYL", formatDiopter(eye.cyl)],
          ["AXIS", formatAxis(eye.axis)],
          ["ADD", formatDiopter(eye.add)],
          ["IOP", formatIop(eye.iop)],
        ].map(([k, v]) => (
          <div key={k}>
            <div className="text-text-tertiary">{k}</div>
            <div className="text-text">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const eyeFields = [
  { key: "sph", label: "Sphere", step: "0.25", placeholder: "-1.25" },
  { key: "cyl", label: "Cylinder", step: "0.25", placeholder: "-0.50" },
  { key: "axis", label: "Axis (°)", step: "1", placeholder: "90" },
  { key: "add", label: "Add", step: "0.25", placeholder: "2.00" },
  { key: "iop", label: "IOP (mmHg)", step: "1", placeholder: "16" },
] as const;

export function EyeHistoryTab({ patientId, isDoctor }: { patientId: string; isDoctor: boolean }) {
  const queryClient = useQueryClient();
  const me = useMe();
  const [addOpen, setAddOpen] = useState(false);

  const records = useQuery({
    queryKey: ["records", patientId],
    queryFn: () => api.patients.records(patientId),
  });
  const patient = useQuery({
    queryKey: ["patient", patientId],
    queryFn: () => api.patients.get(patientId),
  });
  const clinic = useQuery({ queryKey: ["clinic"], queryFn: () => api.settings.getClinic() });
  const prescriptions = useQuery({
    queryKey: ["patient-prescriptions", patientId],
    queryFn: () => api.patients.prescriptions(patientId),
  });

  /** Build the printable prescription document for a given exam record + Rx. */
  const buildDoc = (record: EyeRecord, rx?: Prescription) => ({
    clinic: clinic.data ?? null,
    patientName: patient.data?.fullName ?? "Patient",
    idNumber: patient.data?.idNumber,
    dob: patient.data?.dob,
    phone: patient.data?.phone,
    record,
    doctorName: rx?.doctor?.name ?? me.data?.name ?? "Doctor",
    prescriptionId: rx?.id ?? "—",
    issuedAt: rx?.issuedAt ?? new Date().toISOString(),
  });

  const form = useForm<CreateEyeRecordInput>({
    resolver: zodResolver(CreateEyeRecordSchema),
    defaultValues: { diagnosis: "", rightEye: {}, leftEye: {}, prescription: "", notes: "" },
  });

  const createRecord = useMutation({
    mutationFn: (input: CreateEyeRecordInput) => api.patients.createRecord(patientId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["records", patientId] });
      queryClient.invalidateQueries({ queryKey: ["patient", patientId] });
      setAddOpen(false);
      form.reset();
    },
  });

  const createPrescription = useMutation({
    mutationFn: (record: EyeRecord) => api.prescriptions.create({ patientId, eyeRecordId: record.id }),
    onSuccess: (prescription, record) => {
      queryClient.invalidateQueries({ queryKey: ["prescriptions"] });
      queryClient.invalidateQueries({ queryKey: ["patient-prescriptions", patientId] });
      queryClient.invalidateQueries({ queryKey: ["patient", patientId] });
      openPrescriptionDoc(buildDoc(record, prescription));
    },
  });

  const rxByRecord = (recordId: string): Prescription[] =>
    (prescriptions.data ?? []).filter((rx) => rx.eyeRecordId === recordId);

  if (records.isLoading) return <SkeletonRows rows={3} />;

  return (
    <div className="max-w-3xl">
      {isDoctor && (
        <div className="mb-4 flex justify-end">
          <Button onClick={() => setAddOpen(true)}>
            <FilePlus2 size={15} /> New exam record
          </Button>
        </div>
      )}

      {(records.data ?? []).length === 0 ? (
        <Card className="py-10 text-center text-sm text-text-tertiary">
          <Eye size={28} className="mx-auto mb-2 opacity-50" />
          No eye exam records yet.
        </Card>
      ) : (
        /* timeline */
        <div className="relative space-y-5 pl-6">
          <div
            className="absolute bottom-2 left-2 top-2 w-px"
            style={{ background: "var(--accent-subtle)" }}
          />
          {(records.data ?? []).map((record) => (
            <div key={record.id} className="relative">
              <span className="absolute -left-[21px] top-5 h-3 w-3 rounded-pill border-2 border-accent bg-bg" />
              <Card>
                <div className="mb-3">
                  <div className="text-sm font-semibold">{record.diagnosis}</div>
                  <div className="text-xs text-text-tertiary">
                    {format(new Date(record.visitDate), "EEEE, d MMMM yyyy")}
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <EyeValues label="OD · Right eye" eye={record.rightEye} />
                  <EyeValues label="OS · Left eye" eye={record.leftEye} />
                </div>

                {record.prescription && (
                  <div
                    className="mt-3 rounded-lg border bg-bg px-4 py-2.5"
                    style={{ borderColor: "var(--accent-subtle)" }}
                  >
                    <div className="text-xs font-semibold uppercase tracking-wide text-accent-light">
                      Prescription
                    </div>
                    <p className="mt-0.5 whitespace-pre-wrap text-sm">{record.prescription}</p>
                  </div>
                )}

                {record.notes && (
                  <p className="mt-3 text-sm text-text-secondary">{record.notes}</p>
                )}

                {/* Prescription PDF section */}
                <div
                  className="mt-4 border-t pt-3"
                  style={{ borderColor: "var(--accent-subtle)" }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
                      Prescription PDF
                    </span>
                    {isDoctor && (
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={
                          createPrescription.isPending &&
                          createPrescription.variables?.id === record.id
                        }
                        onClick={() => createPrescription.mutate(record)}
                      >
                        <FileText size={14} /> Generate PDF
                      </Button>
                    )}
                  </div>
                  {rxByRecord(record.id).length === 0 ? (
                    <p className="mt-2 text-xs text-text-tertiary">
                      No prescription PDF generated from this exam yet.
                    </p>
                  ) : (
                    <div className="mt-2 space-y-1.5">
                      {rxByRecord(record.id).map((rx) => (
                        <div
                          key={rx.id}
                          className="flex items-center justify-between gap-2 rounded-lg border bg-bg px-3 py-2"
                          style={{ borderColor: "var(--accent-subtle)" }}
                        >
                          <span className="text-xs text-text-secondary">
                            Rx <span className="font-mono">{rx.id.slice(-8)}</span> · issued{" "}
                            {format(new Date(rx.issuedAt), "d MMM yyyy")}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openPrescriptionDoc(buildDoc(record, rx))}
                          >
                            <Download size={14} /> Download PDF
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            </div>
          ))}
        </div>
      )}
      {createPrescription.isError && (
        <p className="mt-3 text-sm text-danger">
          {createPrescription.error instanceof Error
            ? createPrescription.error.message
            : "Prescription generation failed"}
        </p>
      )}

      {/* new record dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent
          title="New exam record"
          description="OD/OS refraction and intraocular pressure"
          className="max-w-2xl"
        >
          <form
            onSubmit={form.handleSubmit((v) => createRecord.mutate(v))}
            className="space-y-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Visit date</Label>
                <Input type="date" {...form.register("visitDate")} />
              </div>
              <div>
                <Label>Diagnosis</Label>
                <Input {...form.register("diagnosis")} placeholder="Myopia with astigmatism" />
                <FieldError message={form.formState.errors.diagnosis?.message} />
              </div>
            </div>

            {(["rightEye", "leftEye"] as const).map((side) => (
              <fieldset
                key={side}
                className="rounded-lg border p-4"
                style={{ borderColor: "var(--accent-subtle)" }}
              >
                <legend className="px-2 text-xs font-semibold uppercase tracking-wide text-accent-light">
                  {side === "rightEye" ? "OD · Right eye" : "OS · Left eye"}
                </legend>
                <div className="grid grid-cols-5 gap-3">
                  {eyeFields.map((f) => (
                    <div key={f.key}>
                      <Label className="text-xs">{f.label}</Label>
                      <Input
                        type="number"
                        step={f.step}
                        placeholder={f.placeholder}
                        className="font-mono text-xs"
                        {...form.register(`${side}.${f.key}` as const, {
                          setValueAs: (v) => (v === "" || v === null ? null : Number(v)),
                        })}
                      />
                    </div>
                  ))}
                </div>
              </fieldset>
            ))}

            <div>
              <Label>Prescription</Label>
              <Textarea
                {...form.register("prescription")}
                rows={2}
                placeholder="e.g. Single-vision distance glasses, polycarbonate lenses — or Latanoprost 0.005%, 1 drop each eye at night"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea {...form.register("notes")} rows={2} />
            </div>
            {createRecord.isError && (
              <p className="text-sm text-danger">
                {createRecord.error instanceof Error ? createRecord.error.message : "Save failed"}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setAddOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={createRecord.isPending}>
                Save record
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
